import { Divider,Stack } from '@ekhodealer/ekho-common/rev';

import type { OrderPaymentFai } from '@ekhodealer/ekho-common';

import { LineItem } from '@common/components/LineItem';
import { toSentenceCase } from '@common/lib/strings';

interface ReceiptProps {
	receipt: NonNullable<OrderPaymentFai['buyerPayments']['receipt']>;
}

type SimpleReceiptItem = {
	label: string;
	amount: number;
};

type VehicleReceiptItem = {
	label: string;
	amount: number;
	specs?: Array<{ label: string; amount: number }>;
	discounts?: Array<{ label: string; amount: number }>;
};

export const Receipt = ({ receipt }: ReceiptProps) => {
	const sections = [
		receipt.vehicles?.length && {
			key: 'vehicles',
			title: 'Vehicles',
			items: receipt.vehicles as VehicleReceiptItem[],
			hasNestedSpecs: true,
		},
		receipt.pga?.length && {
			key: 'pga',
			title: 'PG&A',
			items: receipt.pga as SimpleReceiptItem[],
		},
		receipt.taxes?.length && {
			key: 'taxes',
			title: 'Taxes',
			items: receipt.taxes as SimpleReceiptItem[],
		},
		receipt.fees?.length && {
			key: 'fees',
			title: 'Fees',
			items: receipt.fees as SimpleReceiptItem[],
		},
	].filter(Boolean) as Array<{
		key: string;
		title: string;
		items: Array<SimpleReceiptItem | VehicleReceiptItem>;
		hasNestedSpecs?: boolean;
	}>;

	const sectionLineItems = sections.map((section) => {
		const total = section.items.reduce((sum, i) => sum + i.amount, 0);

		return (
			<LineItem
				key={section.key}
				title={section.title}
				price={total}
				isToggleable
				subItems={
					section.hasNestedSpecs
						? section.items.map((item, itemIndex) => {
							const vehicle = item as VehicleReceiptItem;

							return (
								<LineItem
									key={`${section.key}-${itemIndex}`}
									title={vehicle.label}
									price={vehicle.amount}
									subItems={[
										...(vehicle.specs?.map((spec, specIndex) => (
											<LineItem
												key={`${section.key}-${itemIndex}-spec-${specIndex}`}
												title={spec.label}
												price={spec.amount}
											/>
										)) ?? []),
										...(vehicle.discounts?.map((discount, discountIndex) => (
											<LineItem
												key={`${section.key}-${itemIndex}-discount-${discountIndex}`}
												title={toSentenceCase(discount.label)}
												price={discount.amount}
											/>
										)) ?? []),
									]}
								/>
							);
						})
						: section.items.map((item, itemIndex) => {
							const simple = item as SimpleReceiptItem;

							return (
								<LineItem
									key={`${section.key}-${itemIndex}`}
									title={toSentenceCase(simple.label)}
									price={simple.amount}
								/>
							);
						})
				}
			/>
		);
	});

	const { grandTotal } = receipt;

	return (
		<Stack itemsSpacing='0'>
			{sectionLineItems.map((item, index) => (
				<Stack key={index} itemsSpacing='0'>
					{index > 0 && <Divider />}
					{item}
				</Stack>
			))}

			{sections.length > 0 && (
				<Stack itemsSpacing='0'>
					<Divider />
					<LineItem title='Total' price={grandTotal} isTotal />
				</Stack>
			)}
		</Stack>
	);
};
